import React from 'react';

import { useForm } from 'react-hook-form';

import { SaveButton } from '../../../../../../components/Clickables/Buttons/SaveButton';
import { DividerWithText } from '../../../../../../components/Divider/DividerWithText';
import { ErrorBanner } from '../../../../../../components/ErrorBanner/ErrorBanner';
import { Heading } from '../../../../../../components/Heading/Heading';
import { HooksFormInput } from '../../../../../../components/Inputs/FormInput/HooksFormInput';
import { HooksFormInputPassword } from '../../../../../../components/Inputs/FormInput/HooksFormInputPassword';
import { HooksFormToggle } from '../../../../../../components/Inputs/FormToggle/HooksFormToggle';
import { Recaptcha } from '../../../../../../components/Recaptcha/Recaptcha';
import { Text } from '../../../../../../components/Text/Text';
import {
  MIN_PASSWORD_LENGTH,
  RECAPTCHA_SITE_KEY,
  SIGNUP_METHODS,
} from '../../../../../../constants/constants';
import type { MachineState, Sender } from '../../../../../../interfaces/machines';
import { TermsOfService } from '../../../../components/TermsOfService/TermsOfService';
import type { Context, Events } from '../../machine/types';

import { SocialProviderArea } from './components/SocialProviderArea/SocialProviderArea';

interface SignUpState {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  acceptsEmail: boolean;
}

interface SignUpProps {
  state: MachineState<Context, Events>;
  send: Sender<Events>;
}

export const SignUp = ({ state, send }: SignUpProps) => {
  const reCaptchaRef = React.useRef<typeof Recaptcha>();
  const { standardAuth, isLoading } = state.context;
  const { errors: submitErrors } = standardAuth;
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpState>();

  const onSubmit = ({ acceptsEmail, email, password, firstName, lastName }: SignUpState): void => {
    send({
      type: 'SIGNUP_VIA_CREDENTIALS',
      data: {
        account: { acceptsEmail, email, password, firstName, lastName },
        OAuth: { method: SIGNUP_METHODS.manual },
        reCaptcha: { ref: reCaptchaRef },
      },
    });
  };

  return (
    <div className="my-auto flex max-w-(--onboarding-flow-width) flex-col p-6">
      <Heading variant="md" className="mb-0 text-center">
        Sign up
      </Heading>
      <SocialProviderArea state={state} send={send} />
      <DividerWithText className="text-midnight my-8">
        <Text as="span" variant="sm" className="text-midnight">
          OR
        </Text>
      </DividerWithText>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* @ts-ignore */}
        <Recaptcha ref={reCaptchaRef as any} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
        <HooksFormInput
          id="email"
          label="Email"
          inputProps={{ type: 'email' }}
          error={errors.email}
          className="mb-4"
          register={register}
          registerOptions={{ required: 'No email was entered.' }}
        />
        <div className="flex flex-wrap justify-between">
          <HooksFormInput
            id="firstName"
            label="First name"
            inputProps={{ type: 'text' }}
            error={errors.firstName}
            className="mb-4 w-[48%]"
            register={register}
            registerOptions={{ required: 'No first name was entered.' }}
          />
          <HooksFormInput
            id="lastName"
            label="Last name"
            inputProps={{ type: 'text' }}
            error={errors.lastName}
            className="mb-4 w-[48%]"
            register={register}
            registerOptions={{ required: 'No last name was entered.' }}
          />
        </div>
        <HooksFormInputPassword
          id="password"
          label="Password"
          error={errors.password}
          className="mb-4"
          register={register}
          watch={watch}
          registerOptions={{
            required: 'No password was entered.',
            minLength: {
              value: MIN_PASSWORD_LENGTH,
              message: `Your password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
            },
          }}
        />
        <div className="mb-4 max-w-(--onboarding-flow-width) md:max-w-(--onboarding-flow-plus-width)">
          <TermsOfService />
        </div>
        {submitErrors.length > 0 && <ErrorBanner errors={submitErrors} />}
        <SaveButton
          isSaving={isLoading}
          type="submit"
          className="my-0 mb-4 w-full"
          savingLabel="Processing..."
        >
          Create account
        </SaveButton>
        <HooksFormToggle
          id="acceptsEmail"
          label="Yes, I would like to receive the latest news and offers from Fishbrain."
          error={errors.acceptsEmail}
          register={register}
          watch={watch}
        />
      </form>
      <Text variant="sm" className="mt-4">
        You will be assigned a username that can be changed in your settings at any time.
      </Text>
    </div>
  );
};
